import type { BroadcastHealth, DimensionHealth } from '@vindral/publisher';
import type { MoqFallbackReason } from './moq-publisher-stats';

/** From here the presenter is offered a manual switch to WebRTC. */
export const MOQ_SLOW_CONNECT_MS = 5_000;
/** Without a first connect by then we switch to WebRTC ourselves. */
export const MOQ_CONNECT_BUDGET_MS = 20_000;
/**
 * The SDK's own retry budget before the first connect. Shorter than the watchdog
 * (MOQ_CONNECT_BUDGET_MS) so the SDK gives up first and publish() rejects with connection_exhausted
 * carrying the real cause; the watchdog stays the hard upper bound for a hung prepare() and slow attempts.
 */
export const MOQ_RECONNECT_BUDGET_MS = 15_000;

/**
 * Before the first connect MoQ gets the reconnect budget; after it, it reconnects forever. A plain drop
 * after having connected is usually the network, which WebRTC would not survive either, and the WebRTC
 * component does not reliably retry a failed initial connect.
 */
export const shouldReconnect = (connectedOnce: boolean, startedAt: number, now: number): boolean => connectedOnce || now - startedAt < MOQ_RECONNECT_BUDGET_MS;

interface CodedError {
    code: string;
    source?: unknown;
}
const isCoded = (value: unknown): value is CodedError => typeof value === 'object' && value !== null && typeof (value as CodedError).code === 'string';

/** The SDK wraps the real cause of a spent retry budget in connection_exhausted; that cause is what we want to see. */
export const underlyingErrorCode = (error: unknown): string | null => {
    if (!isCoded(error)) return null;
    if (error.code === 'connection_exhausted' && isCoded(error.source)) return error.source.code;
    return error.code;
};

/** The server refused our token or channel: a fresh token is needed before any retry, MoQ or WebRTC. */
export const isAuthRejection = (errorCode: string | null): boolean =>
    errorCode === 'unauthorized' || errorCode === 'forbidden' || errorCode === 'invalid_namespace';

export const reasonForStartFailure = (errorCode: string | null): MoqFallbackReason => {
    if (isAuthRejection(errorCode)) return 'rejected';
    return errorCode === 'encoding_failed' ? 'encoder' : 'connect_failed';
};

/** A Publisher that closes without us asking has ended for good, with or without an error (a track can end cleanly). */
export const reasonForClose = (error: unknown): MoqFallbackReason => (error ? 'fatal_after_connect' : 'closed_unexpectedly');

interface EncoderSupport {
    isConfigSupported(config: object): Promise<{ supported?: boolean }>;
}
interface NavigatorBrands {
    userAgentData?: { brands?: ReadonlyArray<{ brand: string }> };
}
interface EncoderEnvironment {
    navigator?: NavigatorBrands;
    VideoEncoder?: EncoderSupport;
    AudioEncoder?: EncoderSupport;
}

/**
 * MoQ publishing works in Chromium browsers only (Chrome, Edge, Brave, Opera), not in Safari or
 * Firefox. Those, like Chrome on iOS (WebKit), have no navigator.userAgentData.
 */
export const isChromiumBrowser = (nav: NavigatorBrands | undefined = globalThis.navigator as NavigatorBrands | undefined): boolean =>
    !!nav?.userAgentData?.brands?.some((b) => b.brand === 'Chromium');

/**
 * Checked before prepare()/publish(), so an unsupported browser or config falls back as 'unsupported'
 * without starting the SDK (since 0.4 it rejects with encoding_failed instead of hanging, see start()).
 */
export const moqSupported = async (video: object, audio: object, env: EncoderEnvironment = globalThis as unknown as EncoderEnvironment): Promise<boolean> => {
    if (!isChromiumBrowser(env.navigator)) return false;
    if (!env.VideoEncoder || !env.AudioEncoder) return false;
    try {
        const [v, a] = await Promise.all([env.VideoEncoder.isConfigSupported(video), env.AudioEncoder.isConfigSupported(audio)]);
        return !!v.supported && !!a.supported;
    } catch {
        return false;
    }
};

const scoreOf = (dimension: DimensionHealth | undefined) =>
    !dimension || dimension.state === 'unknown' || dimension.score === undefined ? 10 : dimension.score;

/** The existing 0-10 indicator: the worse of the track's encoder and the connection, rounded so it does not change every tick. */
export const moqUiScores = (health: BroadcastHealth, connected: boolean): { audioScore: number; videoScore: number } => {
    if (!connected) return { audioScore: 10, videoScore: 10 };
    const connection = scoreOf(health.detail.connection);
    return {
        audioScore: Math.round(Math.min(scoreOf(health.detail.encoder.audio), connection)),
        videoScore: Math.round(Math.min(scoreOf(health.detail.encoder.video), connection))
    };
};
